import React, { useState, useContext, useMemo, useEffect } from "react";
import {
  Dropdown,
  Button,
  Offcanvas,
  Nav,
  Container,
  Navbar,
  Form,
  Alert,
  OverlayTrigger,
  Tooltip,
  Modal,
} from "react-bootstrap";
import NewTermsOfUse from "../../components/ModalData/NewTermsOfUse";
import {
  IconBellFilled,
  IconMenu2,
  IconUserCheck,
  IconLogout,
  IconClock,
} from "@tabler/icons-react";
import ThemeToggle from "../../components/ThemeToggle";
import UserContext from "../../../contexts/UserContext";
import { getPlanLabel } from "../../../utils/PlanUtils";
import logo_symbol from "../../../images/mercadoagentes_thumb.png";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Logout } from "../../../services/LoginServices";
import { Hash } from "../../../helpers/Helpers";
import { WhitelabelLogo } from "../../components/Whitelabel";
import { FEATURE_IDS } from "../../../constants/FeatureIds";
import { FEATURE_SLUGS } from "../../../constants/FeatureSlugs";
import {
  isScalePlan,
  isGrowthPlan,
  isFounderPlan,
} from "../../../utils/PlanUtils";
import OrganizationService from "../../../screens/Organization/services/OrganizationServices";
import Swal from "sweetalert2";

const MobileHeader = ({ sidebarExpanded, setSidebarExpanded }) => {
  const {
    bottyData,
    user,
    hasPermission,
    hasPermissionBySlug,
    getUserPlan,
    getUserPlanCategory,
    permissionsLoading,
    userFeatures,
    isSupportSession,
    isSubaccount,
    isPersonalAccount,
    isAgentsV2Only,
  } = useContext(UserContext);
  const hideAgentsV1 = isAgentsV2Only();
  const navigate = useNavigate();
  const location = useLocation();

  // Buscar categoria do plano do token JWT (substituindo account_type)
  const [currentPlanCategory, setCurrentPlanCategory] = useState(
    getUserPlanCategory() || "client"
  );
  const planCategory = currentPlanCategory;

  const superuser = {
    1: "Admin",
    2: "Manager",
    3: "Staff",
  };

  // Estados para sessão de acesso ao cliente
  const [activeClientSession, setActiveClientSession] = useState(null);
  const [returningToOriginal, setReturningToOriginal] = useState(false);

  // Estado para modal de termos de uso
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Verificar sessão ativa de cliente ao carregar
  useEffect(() => {
    const checkActiveSession = () => {
      const session = OrganizationService.getCurrentSessionInfo();
      setActiveClientSession(session);

      // Gerenciar classe do body
      if (session) {
        document.body.classList.add("has-client-session");
      } else {
        document.body.classList.remove("has-client-session");
      }
    };

    checkActiveSession();

    // Verificar a cada minuto para atualizar o tempo restante
    const interval = setInterval(checkActiveSession, 60000);

    return () => {
      clearInterval(interval);
      // Limpar classe do body ao desmontar
      document.body.classList.remove("has-client-session");
    };
  }, []);

  // Handler para retornar à conta original
  const handleReturnToOriginalAccount = async () => {
    if (!activeClientSession) return;

    const result = await Swal.fire({
      title: "Encerrar Sessão de Suporte",
      html: `
        <p>Deseja retornar à sua conta original?</p>
        <p><small class="text-muted">
          Você estava acessando: <strong>${activeClientSession.clientAccount?.company_name}</strong><br>
          Motivo: ${activeClientSession.reason}
        </small></p>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, retornar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#3b82f6",
    });

    if (!result.isConfirmed) return;

    try {
      setReturningToOriginal(true);

      await OrganizationService.returnToOriginalAccount(
        activeClientSession.sessionId
      );

      // Limpar sessão local
      OrganizationService.clearActiveSession();
      setActiveClientSession(null);

      Swal.fire({
        icon: "success",
        title: "Sessão Encerrada",
        text: "Você retornou à sua conta original.",
        confirmButtonText: "OK",
        timer: 2000,
      }).then(() => {
        // Recarregar a página para aplicar o contexto original
        window.location.reload();
      });
    } catch (error) {
      console.error("Erro ao retornar à conta original:", error);

      let errorMessage = "Erro ao encerrar sessão.";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      Swal.fire({
        icon: "error",
        title: "Erro",
        text: errorMessage,
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setReturningToOriginal(false);
    }
  };

  const toggleMobileMenu = () => {
    setSidebarExpanded(!sidebarExpanded);
  };

  const handleClose = () => setSidebarExpanded(false);

  const handleClick = (path, external = false) => {
    if (external) {
      window.open(path, '_blank');
    } else {
      navigate(path);
    }
    handleClose(); // Fecha o menu após navegar
  };

  const currentPath = location.pathname;

  // Menu estruturado por categorias para Conta Empresa
  const clientMenuItems = useMemo(
    () => [
      {
        category: "ATENDIMENTO",
        items: [
          {
            path: "/service-center",
            icon: "las la-comments",
            label: "Central de Atendimento",
            featureId: FEATURE_IDS.CHAT,
            condition: bottyData?.uid,
          },
        ],
      },
      {
        category: "AGENTES",
        items: [
          {
            path: "/agents",
            icon: "las la-brain",
            label: "Meus Agentes",
            featureId: FEATURE_IDS.MEUS_AGENTES,
            condition: !hideAgentsV1,
          },
          {
            path: "/agents/create",
            icon: "las la-plus-circle",
            label: "Criar Agente 1.0",
            featureId: FEATURE_IDS.CRIAR_AGENTE,
            condition: isSupportSession && !hideAgentsV1,
          },
          {
            path: "/agents-flow",
            icon: "las la-rocket",
            label: "Agentes 2.0",
            badge: "NOVO",
            featureId: FEATURE_IDS.CRIAR_AGENTE,
            condition: !hideAgentsV1,
          },
          {
            path: "/agents-flow",
            icon: "las la-robot",
            label: "Meus Agentes",
            featureId: FEATURE_IDS.MEUS_AGENTES,
            condition: hideAgentsV1,
          },
          {
            path: "/agents-flow/canvas",
            icon: "las la-plus-circle",
            label: "Criar Agente",
            featureId: FEATURE_IDS.CRIAR_AGENTE,
            condition: hideAgentsV1,
          },
        ],
      },
      {
        category: "CANAIS",
        items: [
          {
            path: "/channels",
            icon: "las la-plug",
            label: "Canais",
            featureId: FEATURE_IDS.CANAIS,
          },
          {
            path: "/integrations",
            icon: "las la-network-wired",
            label: "Integrações",
            featureId: FEATURE_IDS.INTEGRACOES,
            condition: !hideAgentsV1,
          },
        ],
      },

      /*  {
        category: "APRENDIZADO",
        items: [
          {
            path: "/courses",
            icon: "las la-university",
            label: "Vídeos Tutoriais",
            condition: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11].includes(getUserPlan()),
          },
        ],
      }, */
      {
        category: "GESTÃO COMERCIAL",
        items: [
          {
            path: "/customers",
            icon: "las la-users",
            label: "Base de Clientes",
            featureId: FEATURE_IDS.BASE_CLIENTES,
          },
          {
            path: "/crm",
            icon: "las la-chart-line",
            label: "Pipeline CRM",
            featureId: FEATURE_IDS.CRM_PIPELINE,
          },
          {
            path: "/reports",
            icon: "las la-chart-pie",
            label: "Relatórios Avançados",
            featureSlug: FEATURE_SLUGS.RELATORIOS_AVANCADOS,
          },
          {
            path: "/calendar",
            icon: "las la-calendar-alt",
            label: "Agenda",
            featureId: FEATURE_IDS.AGENDA,
          },
          // {
          //   path: "/business-metrics",
          //   icon: "las la-chart-bar",
          //   label: "Métricas",
          //   featureId: FEATURE_IDS.METRICAS,
          // },
        ],
      },
      {
        category: "SUPORTE",
        items: [
          {
            path: "/support",
            icon: "las la-headset",
            label: "Suporte",
            featureId: FEATURE_IDS.SUPORTE,
          },
        ],
      },
      {
        category: "TREINAMENTOS",
        items: [
          {
            path: "/training",
            icon: "las la-graduation-cap",
            label: "Treinamentos",
            featureId: FEATURE_IDS.TREINAMENTOS,
            condition: getUserPlan() !== 4 && getUserPlan() !== 3,
          },
          /* {
            path: "/gamification",
            icon: "las la-gamepad",
            label: "Gamificação",
            condition: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11].includes(getUserPlan()),
          }, */
        ],
      },
      {
        category: "CONFIGURAÇÕES",
        items: [
          {
            path: "/business-settings",
            icon: "las la-building",
            label: "Minha Empresa",
            featureId: FEATURE_IDS.MINHA_EMPRESA,
            condition: !hideAgentsV1,
          },
          {
            path: "/users",
            icon: "las la-users",
            label: "Usuários",
            featureId: FEATURE_IDS.USUARIOS,
            condition: !hideAgentsV1,
          },
          // {
          //   path: "/tags",
          //   icon: "las la-tags",
          //   label: "Tags & Categorias",
          //   featureId: FEATURE_IDS.TAGS_CATEGORIAS,
          // },
          {
            path: "/settings",
            icon: "las la-cog",
            label: "Configurações Globais",
            featureId: FEATURE_IDS.CONFIGURACOES_GLOBAIS,
            condition: !hideAgentsV1 && (user?.superuser_level > 0 || isSupportSession),
            alwaysVisible: true,
          },
        ],
      },
    ],
    [bottyData?.uid, getUserPlan, user?.is_superuser, hideAgentsV1]
  );

  const scaleMenuItems = useMemo(
    () => [
      {
        category: "ATENDIMENTO",
        items: [
          {
            path: "/service-center",
            icon: "las la-comments",
            label: "Central de Atendimento",
            featureId: FEATURE_IDS.CHAT,
            condition: bottyData?.uid,
          },
        ],
      },
      {
        category: "AGENTES",
        items: [
          {
            path: "/agents",
            icon: "las la-robot",
            label: "Meus Agentes  1.0",
            featureId: FEATURE_IDS.MEUS_AGENTES,
            condition: !hideAgentsV1,
          },
          {
            path: "/agents/create",
            icon: "las la-plus-circle",
            label: "Criar Agente 1.0",
            featureId: FEATURE_IDS.CRIAR_AGENTE,
            condition: !hideAgentsV1,
          },
          {
            path: "/agents-flow",
            icon: "las la-rocket",
            label: "Agentes 2.0",
            badge: "NOVO",
            featureId: FEATURE_IDS.CRIAR_AGENTE,
            condition: !hideAgentsV1,
          },
          {
            path: "/agents-flow",
            icon: "las la-robot",
            label: "Meus Agentes",
            featureId: FEATURE_IDS.MEUS_AGENTES,
            condition: hideAgentsV1,
          },
          {
            path: "/agents-flow/canvas",
            icon: "las la-plus-circle",
            label: "Criar Agente",
            featureId: FEATURE_IDS.CRIAR_AGENTE,
            condition: hideAgentsV1,
          },
        ],
      },
      {
        category: "CANAIS",
        items: [
          {
            path: "/channels",
            icon: "las la-plug",
            label: "Canais",
            featureId: FEATURE_IDS.CANAIS,
            condition: getUserPlan() !== 6,
          },
          {
            path: "/integrations",
            icon: "las la-network-wired",
            label: "Integrações",
            featureId: FEATURE_IDS.INTEGRACOES,
            condition: !hideAgentsV1,
          },
        ],
      },

      {
        category: "APRENDIZADO",
        items: [
          {
            path: "/courses",
            icon: "las la-university",
            label: "Vídeos Tutoriais",
            condition: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11].includes(getUserPlan()),
          },
        ],
      },
      {
        category: "GESTÃO COMERCIAL",
        items: [
          {
            path: "/customers",
            icon: "las la-users",
            label: "Base de Clientes",
            featureId: FEATURE_IDS.BASE_CLIENTES,
          },
          {
            path: "/crm",
            icon: "las la-chart-line",
            label: "Pipeline CRM",
            featureId: FEATURE_IDS.CRM_PIPELINE,
          },
          {
            path: "/reports",
            icon: "las la-chart-pie",
            label: "Relatórios Avançados",
            featureSlug: FEATURE_SLUGS.RELATORIOS_AVANCADOS,
          },
          {
            path: "/calendar",
            icon: "las la-calendar-alt",
            label: "Agenda",
            featureId: FEATURE_IDS.AGENDA,
          },
        ],
      },
      /*  {
        category: "MARKETPLACE",
        items: [
          {
            path: "/marketplace",
            icon: "las la-store-alt",
            label: "Minhas Publicações",
            featureId: FEATURE_IDS.PUBLICACOES,
          },
          {
            path: "/publications",
            icon: "las la-store",
            label: "Publicações Admin",
            featureId: FEATURE_IDS.PUBLICACOES,
          },
          {
            path: "/marketplace/shop",
            icon: "las la-shopping-bag",
            label: "MercadoShop",
            condition: [2, 5, 9, 10, 11].includes(getUserPlan()),
          },
          {
            path: "/marketplace/leads",
            icon: "las la-users",
            label: "Leads",
            condition: getUserPlan() > 1,
          },
          {
            path: "/marketplace/seller-profile",
            icon: "las la-user-circle",
            label: "Perfil do Vendedor",
            featureId: FEATURE_IDS.PUBLICACOES,
          },
         
        ],
      }, */
      {
        category: "MINHA ORGANIZAÇÃO",
        items: [
          {
            path: "/organization",
            icon: "las la-building",
            label: "Organização",
            featureId: FEATURE_IDS.ORGANIZACAO,
          },
          {
            path: "/organization-clients",
            icon: "las la-users",
            label: "Subcontas",
            featureId: FEATURE_IDS.CLIENTES_ORGANIZACAO,
          },
          {
            path: "/agents-implementation",
            icon: "las la-bolt",
            label: "Implementações",
            featureId: FEATURE_IDS.IMPLEMENTACOES_AGENTES,
          },
          {
            path: "/users",
            icon: "las la-users",
            label: "Usuários",
            featureId: FEATURE_IDS.USUARIOS,
          },
          {
            path: "/metrics",
            icon: "las la-chart-line",
            label: "Métricas",
            featureId: FEATURE_IDS.METRICAS,
          },
        ],
      },
      {
        category: "FINANCEIRO",
        items: [
          {
            path: "/my-plan",
            icon: "las la-crown",
            label: "Meu Plano",
            featureId: FEATURE_IDS.FATURAMENTO,
            condition: user?.superuser_level === 1,
          },
          {
            path: "/billing",
            icon: "las la-credit-card",
            label: "Faturamento",
            featureId: FEATURE_IDS.FATURAMENTO,
            condition: user?.superuser_level === 1,
          },
          {
            path: "/subscriptions",
            icon: "las la-file-invoice-dollar",
            label: "Minhas Assinaturas",
            featureId: FEATURE_IDS.ASSINATURAS,
            condition: user?.superuser_level === 1,
          },
        ],
      },
      {
        category: "SUPORTE",
        items: [
          {
            path: "/support",
            icon: "las la-headset",
            label: "Suporte Clientes",
            featureId: FEATURE_IDS.SUPORTE,
          },
          {
            path: "https://wa.me/5547991360752?text=Ol%C3%A1%2C%20sou%20cliente%20e%20preciso%20de%20suporte",
            icon: "las la-life-ring",
            label: "Suporte Plataforma",
            external: true,
          },
        ],
      },
      {
        category: "TREINAMENTOS",
        items: [
          {
            path: "/training",
            icon: "las la-graduation-cap",
            label: "Meus Treinamentos",
            featureId: FEATURE_IDS.TREINAMENTOS,
            condition: getUserPlan() !== 4 && getUserPlan() !== 3,
          },
          {
            path: "/training/manage",
            icon: "las la-cog",
            label: "Gerenciar Treinamentos",
            featureId: FEATURE_IDS.GERENCIAR_TREINAMENTOS,
            condition: getUserPlan() !== 4 && getUserPlan() !== 3,
          },
          /* {
            path: "/gamification",
            icon: "las la-gamepad",
            label: "Gamificação",
            condition: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11].includes(getUserPlan()),
          }, */
        ],
      },
      {
        category: "CONFIGURAÇÕES",
        items: [
          {
            path: "/business-settings",
            icon: "las la-building",
            label: "Minha Empresa",
            featureId: FEATURE_IDS.MINHA_EMPRESA,
            condition: !hideAgentsV1,
          },
          {
            path: "/settings",
            icon: "las la-cog",
            label: "Configurações Globais",
            featureId: FEATURE_IDS.CONFIGURACOES_GLOBAIS,
            condition: !hideAgentsV1 && (user?.superuser_level > 0 || isSupportSession),
            alwaysVisible: true,
          },
        ],
      },
    ],
    [bottyData?.uid, getUserPlan, user?.is_superuser, hideAgentsV1]
  );

  // Menu estruturado para Plano PRO (funcionalidades específicas)
  const proMenuItems = useMemo(
    () => [
      {
        category: "ATENDIMENTO",
        items: [
          {
            path: "/service-center",
            icon: "las la-comments",
            label: "Central de Atendimento",
            featureId: FEATURE_IDS.CHAT,
            condition: bottyData?.uid,
          },
        ],
      },
      {
        category: "AGENTES",
        items: [
          {
            path: "/agents",
            icon: "las la-robot",
            label: "Meus Agentes",
            featureId: FEATURE_IDS.MEUS_AGENTES,
            condition: !hideAgentsV1,
          },
          {
            path: "/agents/create",
            icon: "las la-plus-circle",
            label: "Criar Agente 1.0",
            featureId: FEATURE_IDS.CRIAR_AGENTE,
            condition: !hideAgentsV1,
          },
          {
            path: "/agents-flow",
            icon: "las la-rocket",
            label: "Agentes 2.0",
            badge: "NOVO",
            featureId: FEATURE_IDS.CRIAR_AGENTE,
            condition: !hideAgentsV1,
          },
          {
            path: "/agents-flow",
            icon: "las la-robot",
            label: "Meus Agentes",
            featureId: FEATURE_IDS.MEUS_AGENTES,
            condition: hideAgentsV1,
          },
          {
            path: "/agents-flow/canvas",
            icon: "las la-plus-circle",
            label: "Criar Agente",
            featureId: FEATURE_IDS.CRIAR_AGENTE,
            condition: hideAgentsV1,
          },
        ],
      },
      {
        category: "CANAIS",
        items: [
          {
            path: "/channels",
            icon: "las la-plug",
            label: "Canais",
            featureId: FEATURE_IDS.CANAIS,
            condition: getUserPlan() !== 6,
          },
          {
            path: "/integrations",
            icon: "las la-network-wired",
            label: "Integrações",
            featureId: FEATURE_IDS.INTEGRACOES,
            condition: !hideAgentsV1,
          },
        ],
      },

      {
        category: "APRENDIZADO",
        items: [
          {
            path: "/courses",
            icon: "las la-university",
            label: "Vídeos Tutoriais",
            condition: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11].includes(getUserPlan()),
          },
        ],
      },
      {
        category: "GESTÃO COMERCIAL",
        items: [
          {
            path: "/crm",
            icon: "las la-chart-line",
            label: "Pipeline CRM",
            featureId: FEATURE_IDS.CRM_PIPELINE,
          },
          {
            path: "/reports",
            icon: "las la-chart-pie",
            label: "Relatórios Avançados",
            featureSlug: FEATURE_SLUGS.RELATORIOS_AVANCADOS,
          },
          {
            path: "/calendar",
            icon: "las la-calendar-alt",
            label: "Agenda",
            featureId: FEATURE_IDS.AGENDA,
          },
        ],
      },
      {
        category: "MARKETPLACE",
        items: [
          {
            path: "/marketplace",
            icon: "las la-store-alt",
            label: "Minhas Publicações",
            featureId: FEATURE_IDS.PUBLICACOES,
          },
          {
            path: "/publications",
            icon: "las la-store",
            label: "Publicações Admin",
            featureId: FEATURE_IDS.PUBLICACOES,
          },
          {
            path: "/marketplace/shop",
            icon: "las la-shopping-bag",
            label: "MercadoShop",
            condition: [2, 5, 9, 10, 11].includes(getUserPlan()),
          },
          {
            path: "/marketplace/leads",
            icon: "las la-users",
            label: "Leads",
            condition: getUserPlan() > 1,
          },
          {
            path: "/marketplace/seller-profile",
            icon: "las la-user-circle",
            label: "Perfil do Vendedor",
            featureId: FEATURE_IDS.PUBLICACOES,
          },
          /* {
          path: "/statistics",
          icon: "las la-chart-bar",
          label: "Estatísticas",
        }, */
        ],
      },
      {
        category: "MINHA ORGANIZAÇÃO",
        items: [
          {
            path: "/organization",
            icon: "las la-building",
            label: "Organização",
            featureId: FEATURE_IDS.ORGANIZACAO,
          },
          {
            path: "/organization-clients",
            icon: "las la-users",
            label: "Clientes",
            featureId: FEATURE_IDS.CLIENTES_ORGANIZACAO,
          },
          {
            path: "/agents-implementation",
            icon: "las la-bolt",
            label: "Implementações",
            featureId: FEATURE_IDS.IMPLEMENTACOES_AGENTES,
          },
          {
            path: "/users",
            icon: "las la-users",
            label: "Usuários",
            featureId: FEATURE_IDS.USUARIOS,
          },
          {
            path: "/metrics",
            icon: "las la-chart-line",
            label: "Métricas",
            featureId: FEATURE_IDS.METRICAS,
          },
        ],
      },
      {
        category: "SUPORTE",
        items: [
          {
            path: "/support",
            icon: "las la-headset",
            label: "Suporte Clientes",
            featureId: FEATURE_IDS.SUPORTE,
          },
          {
            path: "https://wa.me/5547991360752?text=Ol%C3%A1%2C%20sou%20cliente%20e%20preciso%20de%20suporte",
            icon: "las la-life-ring",
            label: "Suporte Plataforma",
            external: true,
          },
        ],
      },
      {
        category: "TREINAMENTOS",
        items: [
          {
            path: "/training",
            icon: "las la-graduation-cap",
            label: "Meus Treinamentos",
            featureId: FEATURE_IDS.TREINAMENTOS,
            condition: getUserPlan() !== 4 && getUserPlan() !== 3,
          },
          {
            path: "/training/manage",
            icon: "las la-cog",
            label: "Gerenciar Treinamentos",
            featureId: FEATURE_IDS.GERENCIAR_TREINAMENTOS,
            condition: getUserPlan() !== 4 && getUserPlan() !== 3,
          },
        ],
      },
      {
        category: "CONFIGURAÇÕES",
        items: [
          {
            path: "/business-settings",
            icon: "las la-building",
            label: "Minha Empresa",
            featureId: FEATURE_IDS.MINHA_EMPRESA,
            condition: !hideAgentsV1,
          },
          {
            path: "/settings",
            icon: "las la-cog",
            label: "Configurações Globais",
            featureId: FEATURE_IDS.CONFIGURACOES_GLOBAIS,
            condition: !hideAgentsV1 && (user?.superuser_level > 0 || isSupportSession),
            alwaysVisible: true,
          },
        ],
      },
    ],
    [bottyData?.uid, getUserPlan, user?.is_superuser, hideAgentsV1]
  );

  // Menu estruturado para Plano On Demand (funcionalidades básicas)
  const starterMenuItems = useMemo(
    () => [
      {
        category: "ATENDIMENTO",
        items: [
          {
            path: "/service-center",
            icon: "las la-comments",
            label: "Central de Atendimento",
            featureId: FEATURE_IDS.CHAT,
            condition: bottyData?.uid,
          },
        ],
      },
      {
        category: "AGENTES",
        items: [
          {
            path: "/agents",
            icon: "las la-robot",
            label: "Meus Agentes",
            featureId: FEATURE_IDS.MEUS_AGENTES,
            condition: !hideAgentsV1,
          },
          {
            path: "/agents/create",
            icon: "las la-plus-circle",
            label: "Criar Agente 1.0",
            featureId: FEATURE_IDS.CRIAR_AGENTE,
            condition: !hideAgentsV1,
          },
          {
            path: "/agents-flow",
            icon: "las la-rocket",
            label: "Agentes 2.0",
            badge: "NOVO",
            featureId: FEATURE_IDS.CRIAR_AGENTE,
            condition: !hideAgentsV1,
          },
          {
            path: "/agents-flow",
            icon: "las la-robot",
            label: "Meus Agentes",
            featureId: FEATURE_IDS.MEUS_AGENTES,
            condition: hideAgentsV1,
          },
          {
            path: "/agents-flow/canvas",
            icon: "las la-plus-circle",
            label: "Criar Agente",
            featureId: FEATURE_IDS.CRIAR_AGENTE,
            condition: hideAgentsV1,
          },
        ],
      },
      {
        category: "CANAIS",
        items: [
          {
            path: "/channels",
            icon: "las la-plug",
            label: "Canais",
            featureId: FEATURE_IDS.CANAIS,
            condition: getUserPlan() !== 6,
          },
          {
            path: "/integrations",
            icon: "las la-network-wired",
            label: "Integrações",
            featureId: FEATURE_IDS.INTEGRACOES,
            condition: !hideAgentsV1,
          },
        ],
      },

      {
        category: "APRENDIZADO",
        items: [
          {
            path: "/courses",
            icon: "las la-university",
            label: "Vídeos Tutoriais",
            condition: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11].includes(getUserPlan()),
          },
        ],
      },
      {
        category: "GESTÃO COMERCIAL",
        items: [
          {
            path: "/customers",
            icon: "las la-users",
            label: "Base de Clientes",
            featureId: FEATURE_IDS.BASE_CLIENTES,
          },
          {
            path: "/crm",
            icon: "las la-chart-line",
            label: "Pipeline CRM",
            featureId: FEATURE_IDS.CRM_PIPELINE,
          },
          {
            path: "/reports",
            icon: "las la-chart-pie",
            label: "Relatórios Avançados",
            featureSlug: FEATURE_SLUGS.RELATORIOS_AVANCADOS,
          },
          {
            path: "/calendar",
            icon: "las la-calendar-alt",
            label: "Agenda",
            featureId: FEATURE_IDS.AGENDA,
          },
        ],
      },
      /*  {
        category: "MARKETPLACE",
        items: [
          {
            path: "/marketplace",
            icon: "las la-store-alt",
            label: "Minhas Publicações",
            featureId: FEATURE_IDS.PUBLICACOES,
          },
          {
            path: "/publications",
            icon: "las la-store",
            label: "Publicações Admin",
            featureId: FEATURE_IDS.PUBLICACOES,
          },
          {
            path: "/marketplace/shop",
            icon: "las la-shopping-bag",
            label: "MercadoShop",
            condition: [2, 5, 9, 10, 11].includes(getUserPlan()),
          },
          {
            path: "/marketplace/leads",
            icon: "las la-users",
            label: "Leads",
            condition: getUserPlan() > 1,
          },
          {
            path: "/marketplace/seller-profile",
            icon: "las la-user-circle",
            label: "Perfil do Vendedor",
            featureId: FEATURE_IDS.PUBLICACOES,
          },
         
        ],
      }, */
      {
        category: "MINHA ORGANIZAÇÃO",
        items: [
          {
            path: "/organization",
            icon: "las la-building",
            label: "Organização",
            featureId: FEATURE_IDS.ORGANIZACAO,
          },
          {
            path: "/organization-clients",
            icon: "las la-users",
            label: "Subcontas",
            featureId: FEATURE_IDS.CLIENTES_ORGANIZACAO,
          },
          {
            path: "/agents-implementation",
            icon: "las la-bolt",
            label: "Implementações",
            featureId: FEATURE_IDS.IMPLEMENTACOES_AGENTES,
          },
          {
            path: "/users",
            icon: "las la-users",
            label: "Usuários",
            featureId: FEATURE_IDS.USUARIOS,
          },
          {
            path: "/metrics",
            icon: "las la-chart-line",
            label: "Métricas",
            featureId: FEATURE_IDS.METRICAS,
          },
        ],
      },
      {
        category: "FINANCEIRO",
        items: [
          {
            path: "/my-plan",
            icon: "las la-crown",
            label: "Meu Plano",
            featureId: FEATURE_IDS.FATURAMENTO,
            condition: user?.superuser_level === 1,
          },
          {
            path: "/billing",
            icon: "las la-credit-card",
            label: "Faturamento",
            featureId: FEATURE_IDS.FATURAMENTO,
            condition: user?.superuser_level === 1,
          },
          {
            path: "/subscriptions",
            icon: "las la-file-invoice-dollar",
            label: "Minhas Assinaturas",
            featureId: FEATURE_IDS.ASSINATURAS,
            condition: user?.superuser_level === 1,
          },
        ],
      },
      {
        category: "SUPORTE",
        items: [
          {
            path: "https://wa.me/5547991360752?text=Ol%C3%A1%2C%20sou%20cliente%20e%20preciso%20de%20suporte",
            icon: "las la-life-ring",
            label: "Suporte Plataforma",
            external: true,
          },
        ],
      },

      {
        category: "CONFIGURAÇÕES",
        items: [
          {
            path: "/business-settings",
            icon: "las la-building",
            label: "Minha Empresa",
            featureId: FEATURE_IDS.MINHA_EMPRESA,
            condition: !hideAgentsV1,
          },
          {
            path: "/settings",
            icon: "las la-cog",
            label: "Configurações Globais",
            featureId: FEATURE_IDS.CONFIGURACOES_GLOBAIS,
            condition: !hideAgentsV1 && (user?.superuser_level > 0 || isSupportSession),
            alwaysVisible: true,
          },
        ],
      },
    ],
    [bottyData?.uid, getUserPlan, user?.is_superuser, hideAgentsV1]
  );

  // Menu para contas de uso pessoal (sem organização)
  const personalMenuItems = useMemo(
    () =>
      starterMenuItems
        .filter((section) => section.category !== "MINHA ORGANIZAÇÃO")
        .map((section) => {
          if (section.category === "APPS & INTEGRAÇÕES") {
            return {
              ...section,
              items: section.items.filter(
                (item) => item.featureId !== FEATURE_IDS.CANAIS
              ),
            };
          }
          return section;
        }),
    [starterMenuItems]
  );

  // Handler para mudança de categoria de plano
  const handlePlanCategoryChange = (newCategory) => {
    setCurrentPlanCategory(newCategory);
  };

  // Memoizar o menu baseado na categoria do plano
  const currentMenuItems = useMemo(() => {
    // Contas pessoais usam menu sem organização independente do plano
    if (isPersonalAccount()) {
      return personalMenuItems;
    }

    switch (planCategory) {
      case "client":
        return clientMenuItems;
      case "founder":
      case "scale":
      case "white-label":
      case "wl-plus":
      case "wl-pro":
        return scaleMenuItems;
      case "pro":
        return proMenuItems;
      case "starter":
        return starterMenuItems;
      case "free":
      case "personal-starter":
      case "personal-business":
        return personalMenuItems;
      default:
        return clientMenuItems;
    }
  }, [
    planCategory,
    clientMenuItems,
    scaleMenuItems,
    proMenuItems,
    starterMenuItems,
    personalMenuItems,
    isPersonalAccount,
  ]);

  // Menu de administração para usuários is_staff
  const adminMenuItems = useMemo(
    () => [
      {
        category: "ADMINISTRAÇÃO GLOBAL",
        items: [
          {
            path: "/admin/users",
            icon: "las la-users-cog",
            label: "Usuários",
            featureId: FEATURE_IDS.ADMIN_USUARIOS,
            condition: user?.is_staff,
          },
          {
            path: "/admin/training/manage",
            icon: "las la-graduation-cap",
            label: "Gerenciar Treinamentos",
            featureId: FEATURE_IDS.ADMIN_TREINAMENTOS,
            condition: user?.is_staff && getUserPlan() !== 4,
          },
          {
            path: "/admin/courses",
            icon: "las la-university",
            label: "Aprendizado",
            featureId: FEATURE_IDS.ADMIN_CURSOS,
            condition: user?.is_staff,
          },
          {
            path: "/admin/marketplace/banners",
            icon: "las la-image",
            label: "Banners Marketplace",
            condition: user?.is_staff && user?.superuser_level === 1,
          },
        ],
      },
    ],
    [user?.is_staff, user?.superuser_level, getUserPlan]
  );

  const renderOffcanvasMenuItems = () => {
    // Se o usuário é staff, mostrar apenas o menu de administração
    if (user?.is_staff) {
      return (
        <>
          {/* Dashboard sempre no topo */}
          <Nav.Item className="mb-2">
            <Nav.Link
              className={`d-flex align-items-center py-2 px-3 rounded ${
                currentPath === "/" ? "bg-primary text-white" : "text-dark"
              }`}
              onClick={() => handleClick("/")}
              style={{ cursor: "pointer" }}
            >
              <i className="las la-home me-2" style={{ fontSize: "18px" }} />
              <span>Dashboard</span>
            </Nav.Link>
          </Nav.Item>

          <hr className="my-3" />

          {/* Menu de Administração para is_staff */}
          {adminMenuItems
            .map((section) => {
              const visibleItems = section.items.filter((item) => {
                if (item.condition !== undefined && !item.condition) {
                  return false;
                }

                // Items marcados como alwaysVisible são exibidos para todos
                if (item.alwaysVisible) {
                  return true;
                }

                if (item.featureSlug) {
                  if (permissionsLoading) {
                    return false;
                  }
                  if (user?.superuser_level === 1) {
                    return true;
                  }
                  return hasPermissionBySlug(item.featureSlug, "read");
                }

                if (item.featureId) {
                  // Se permissões ainda estão carregando, não mostrar
                  if (permissionsLoading) {
                    return false;
                  }
                  // Se userFeatures está vazio/null, negar acesso (exceto para admin)
                  if (!userFeatures || Object.keys(userFeatures).length === 0) {
                    if (user?.superuser_level === 1) {
                      return true; // Admin sempre tem acesso
                    }
                    return false;
                  }
                  return hasPermission(item.featureId, "read");
                }
                return true;
              });

              if (visibleItems.length === 0) {
                return null;
              }

              return { ...section, items: visibleItems };
            })
            .filter(Boolean)
            .map((section, sectionIndex) => (
              <div key={`admin-${sectionIndex}`} className="mb-4">
                <h6
                  className="text-warning text-uppercase fw-bold px-3 mb-2 admin-category"
                  style={{ fontSize: "12px" }}
                >
                  {section.category}
                </h6>
                <Nav className="flex-column">
                  {section.items.map((item, itemIndex) => {
                    const currentPlan = getUserPlan();
                    const isBlockedByPlan = false;
                    const isShopBlocked =
                      item.path === "/marketplace/shop" &&
                      ![2, 5, 9, 10, 11].includes(currentPlan);
                    const isItemBlocked = isBlockedByPlan || isShopBlocked;

                    const menuItem = (
                      <Nav.Item key={itemIndex}>
                        <Nav.Link
                          className={`d-flex align-items-center py-2 px-3 rounded admin-menu-item ${
                            currentPath === item.path
                              ? "bg-warning text-dark"
                              : "text-dark"
                          } ${isItemBlocked ? "plan-blocked" : ""}`}
                          onClick={() =>
                            isItemBlocked ? null : handleClick(item.path, item?.external)
                          }
                          title={
                            isItemBlocked ? "Faça um upgrade para liberar" : ""
                          }
                          style={{
                            cursor: isItemBlocked ? "not-allowed" : "pointer",
                            opacity: isItemBlocked ? 0.5 : 1,
                            color: isItemBlocked ? "#999" : "",
                          }}
                        >
                          <i
                            className={`${item.icon} me-2`}
                            style={{ fontSize: "18px" }}
                          />
                          <span>{item.label}</span>
                          {item.badge && <span className={`menu-beta-badge menu-badge--${item.badge.toLowerCase()}`}>{item.badge}</span>}
                          {isItemBlocked && (
                            <i
                              className="las la-lock ms-auto"
                              style={{ fontSize: "14px" }}
                            />
                          )}
                        </Nav.Link>
                      </Nav.Item>
                    );

                    if (isItemBlocked) {
                      return (
                        <OverlayTrigger
                          key={itemIndex}
                          placement="left"
                          overlay={
                            <Tooltip id={`tooltip-mobile-admin-${itemIndex}`}>
                              Faça um upgrade para liberar
                            </Tooltip>
                          }
                          trigger={["hover", "focus"]}
                        >
                          <div style={{ width: "100%" }}>{menuItem}</div>
                        </OverlayTrigger>
                      );
                    }

                    return menuItem;
                  })}
                </Nav>
              </div>
            ))}
        </>
      );
    }

    // Menu padrão para usuários não-staff
    return (
      <>
        {/* Dashboard sempre no topo */}
        <Nav.Item className="mb-2">
          <Nav.Link
            className={`d-flex align-items-center py-2 px-3 rounded ${
              currentPath === "/" ? "bg-primary text-white" : "text-dark"
            }`}
            onClick={() => handleClick("/")}
            style={{ cursor: "pointer" }}
          >
            <i className="las la-home me-2" style={{ fontSize: "18px" }} />
            <span>Dashboard</span>
          </Nav.Link>
        </Nav.Item>

        <hr className="my-3" />

        {currentMenuItems
          .map((section) => {
            // Filtrar itens que o usuário tem permissão
            const visibleItems = section.items.filter((item) => {
              // Verifica condição se existir
              if (item.condition !== undefined && !item.condition) {
                return false;
              }

              // Items marcados como alwaysVisible são exibidos para todos
              if (item.alwaysVisible) {
                return true;
              }

              // ========== LÓGICA ESPECIAL PARA SUBACCOUNTS ==========
              if (isSubaccount()) {
                if (permissionsLoading) return false;
                if (isSupportSession || user?.superuser_level === 1) return true;
                if (item.featureSlug) {
                  return hasPermissionBySlug(item.featureSlug, "read");
                }
                if (!item.featureId) return false;
                if (!userFeatures || !userFeatures[item.featureId]) return false;
                return userFeatures[item.featureId].permissions?.read === true;
              }
              // ========== FIM LÓGICA SUBACCOUNTS ==========

              // Verifica permissão por Feature SLUG se existir (preferido pra features novas)
              if (item.featureSlug) {
                if (permissionsLoading) {
                  return false;
                }
                if (user?.superuser_level === 1) {
                  return true;
                }
                return hasPermissionBySlug(item.featureSlug, "read");
              }

              // Verifica permissão por Feature ID se existir
              if (item.featureId) {
                // Se permissões ainda estão carregando, não mostrar
                if (permissionsLoading) {
                  return false;
                }
                // Se userFeatures está vazio/null, negar acesso (exceto para admin)
                if (!userFeatures || Object.keys(userFeatures).length === 0) {
                  if (user?.superuser_level === 1) {
                    return true; // Admin sempre tem acesso
                  }
                  return false;
                }
                return hasPermission(item.featureId, "read");
              }

              // Para usuários level 0, itens sem featureId são NEGADOS (não mais backward compatibility)
              if (user?.superuser_level === 0) {
                return false;
              }

              return user?.is_superuser || false;
            });

            // Se não há itens visíveis, não renderizar a categoria
            if (visibleItems.length === 0) {
              return null;
            }

            return { ...section, items: visibleItems };
          })
          .filter(Boolean) // Remove seções null/undefined
          .map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-4">
              <h6
                className="text-muted text-uppercase fw-bold px-3 mb-2"
                style={{ fontSize: "12px" }}
              >
                {section.category}
              </h6>
              <Nav className="flex-column">
                {section.items.map((item, itemIndex) => {
                  const currentPlan = getUserPlan();
                  const isBlockedByPlan = false;
                  const isShopBlocked =
                    item.path === "/marketplace/shop" &&
                    ![2, 5, 9, 10, 11].includes(currentPlan);
                  const isItemBlocked = isBlockedByPlan || isShopBlocked;

                  const menuItem = (
                    <Nav.Item key={itemIndex}>
                      <Nav.Link
                        className={`d-flex align-items-center py-2 px-3 rounded ${
                          currentPath === item.path
                            ? "bg-primary text-white"
                            : "text-dark"
                        } ${isItemBlocked ? "plan-blocked" : ""}`}
                        onClick={() =>
                          isItemBlocked ? null : handleClick(item.path, item?.external)
                        }
                        title={
                          isItemBlocked ? "Faça um upgrade para liberar" : ""
                        }
                        style={{
                          cursor: isItemBlocked ? "not-allowed" : "pointer",
                          opacity: isItemBlocked ? 0.5 : 1,
                          color: isItemBlocked ? "#999" : "",
                        }}
                      >
                        <i
                          className={`${item.icon} me-2`}
                          style={{ fontSize: "18px" }}
                        />
                        <span>{item.label}</span>
                        {item.badge && <span className={`menu-beta-badge menu-badge--${item.badge.toLowerCase()}`}>{item.badge}</span>}
                        {isItemBlocked && (
                          <i
                            className="las la-lock ms-auto"
                            style={{ fontSize: "14px" }}
                          />
                        )}
                      </Nav.Link>
                    </Nav.Item>
                  );

                  if (isItemBlocked) {
                    return (
                      <OverlayTrigger
                        key={itemIndex}
                        placement="left"
                        overlay={
                          <Tooltip id={`tooltip-mobile-${itemIndex}`}>
                            Faça um upgrade para liberar
                          </Tooltip>
                        }
                        trigger={["hover", "focus"]}
                      >
                        <div style={{ width: "100%" }}>{menuItem}</div>
                      </OverlayTrigger>
                    );
                  }

                  return menuItem;
                })}
              </Nav>
            </div>
          ))}
      </>
    );
  };

  return (
    <>
      {/* Banner de sessão ativa do cliente - Mobile */}
      {activeClientSession && (
        <Alert
          variant="info"
          className="client-session-banner-mobile d-lg-none d-flex align-items-center justify-content-between mb-0 rounded-0 border-0"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            fontSize: "13px",
            padding: "8px 15px",
          }}
        >
          <div className="d-flex align-items-center flex-grow-1">
            <IconUserCheck size={16} className="me-2 text-primary" />
            <div className="text-truncate">
              <strong>Suporte:</strong>{" "}
              {activeClientSession.clientAccount?.company_name}
              <div
                className={`session-time-indicator d-flex align-items-center text-muted ${
                  activeClientSession.isExpiringSoon ? "expiring" : ""
                }`}
                style={{ fontSize: "11px" }}
              >
                <IconClock size={12} className="me-1" />
                {activeClientSession.timeLeftMinutes}min
                {activeClientSession.isExpiringSoon && (
                  <span className="text-warning ms-1">⚠️</span>
                )}
              </div>
            </div>
          </div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleReturnToOriginalAccount}
            disabled={returningToOriginal}
            className="session-end-btn"
            style={{ fontSize: "11px", padding: "4px 8px" }}
          >
            <IconLogout size={12} />
          </Button>
        </Alert>
      )}

      {/* Navbar Bootstrap para Mobile */}
      <Navbar
        bg="primary"
        variant="primary"
        className="d-lg-none fixed-top px-3"
        style={{
          top: activeClientSession ? "44px" : "0", // Ajustar posição se houver banner
        }}
      >
        <Container fluid>
          <div className="d-flex align-items-center">
            <Button
              variant="outline-light"
              size="sm"
              className="me-3 border-0"
              onClick={toggleMobileMenu}
            >
              <IconMenu2 size={20} />
            </Button>
            {/* <Navbar.Brand className="mb-0">
              <WhitelabelLogo
                type="mobile"
                className="mobile-logo"
                fallback={
                  <img
                    src={logo_symbol}
                    alt="logo"
                    style={{ height: "32px", width: "auto" }}
                  />
                }
              />
            </Navbar.Brand> */}
          </div>

          <div className="d-flex align-items-center">
            {/* <ThemeToggle className="me-3" size={18} /> */}
            <Button variant="outline-light" size="sm" className="me-3 border-0">
              <IconBellFilled size={18} />
            </Button>

            <Dropdown align="end">
              <Dropdown.Toggle
                as="div"
                className="d-flex align-items-center"
                style={{ cursor: "pointer" }}
              >
                <img
                  src={
                    user?.user_photo
                      ? !user?.user_photo.includes("https://")
                        ? `https://mercadoagentes.s3.amazonaws.com/${user.user_photo}`
                        : user?.user_photo
                      : require("../../../images/user.png")
                  }
                  alt="profile"
                  className="rounded-circle"
                  style={{ width: "32px", height: "32px" }}
                />
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item
                  as={Link}
                  to={`/user-details/${user?.user_id || ""}`}
                  className="d-flex align-items-center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="text-primary me-2"
                    width={16}
                    height={16}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx={12} cy={7} r={4} />
                  </svg>
                  Conta
                </Dropdown.Item>
                {!isSubaccount() && (
                  <>
                    <Dropdown.Item
                      onClick={() => setShowTermsModal(true)}
                      className="d-flex align-items-center"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="text-info me-2"
                        width={16}
                        height={16}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <polyline points="10 9 9 9 8 9" />
                      </svg>
                      Termos de Uso
                    </Dropdown.Item>
                    <Dropdown.Divider />
                  </>
                )}
                <Dropdown.Item
                  onClick={Logout}
                  className="d-flex align-items-center text-danger"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="me-2"
                    width={16}
                    height={16}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1={21} y1={12} x2={9} y2={12} />
                  </svg>
                  Sair
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </Container>
      </Navbar>

      {/* Offcanvas Menu */}
      <Offcanvas show={sidebarExpanded} onHide={handleClose} placement="start">
        <Offcanvas.Header closeButton className="bg-light">
          <Offcanvas.Title className="fw-bold">Menu</Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body className="p-0">
          {/* Select de Conta - Habilitado para planos de agência */}
          {getUserPlan() > 1 && (
            <div className="p-3 bg-light border-bottom">
              <label className="profile-select-label d-flex justify-content-between">
                Meu plano
                {planCategory === "founder" && (
                  <span
                    className="founder-level-badge pr-3"
                    style={{ paddingRight: 10 }}
                  >
                    Lvl. 10 🏆
                  </span>
                )}
              </label>
              <div className={`plan-display-container plan-${planCategory}`}>
                <i
                  className={`plan-icon ${
                    planCategory === "founder"
                      ? "las la-crown"
                      : planCategory === "scale"
                      ? "las la-rocket"
                      : planCategory === "growth"
                      ? "las la-chart-line"
                      : planCategory === "pro"
                      ? "las la-chart-line"
                      : planCategory === "starter"
                      ? "las la-play"
                      : planCategory === "free"
                      ? "las la-gift"
                      : planCategory === "personal-starter"
                      ? "las la-star"
                      : planCategory === "personal-business"
                      ? "las la-briefcase"
                      : planCategory === "white-label"
                      ? "las la-tags"
                      : planCategory === "wl-plus"
                      ? "las la-rocket"
                      : planCategory === "wl-pro"
                      ? "las la-crown"
                      : "las la-user"
                  }`}
                ></i>
                <span className="plan-text">
                  {getPlanLabel(getUserPlan())}
                </span>
                {getUserPlan() === 2 && (
                  <img
                    src="/imgs/selo-fundador.png"
                    alt="Selo Fundador"
                    className="founder-crown"
                  />
                )}
              </div>
            </div>
          )}

          {/* Menu Items */}
          <div className="p-3">{renderOffcanvasMenuItems()}</div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Modal de Termos de Uso */}
      <Modal
        show={showTermsModal}
        onHide={() => setShowTermsModal(false)}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Termos de Uso</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <NewTermsOfUse setShowModal={setShowTermsModal} />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default MobileHeader;